import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import { NAV_LINKS, SOCIETY } from "@/lib/data";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-brand-ivory/85 backdrop-blur-xl border-b border-brand-sage/70 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-baseline gap-2 group"
        >
          <span
            className={`font-display text-2xl md:text-3xl font-semibold tracking-tight ${
              scrolled ? "text-brand-forest" : "text-brand-forest"
            }`}
          >
            iTowers
          </span>
          <span className="font-display italic text-brand-gold text-xl md:text-2xl">
            Exente
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium tracking-wide transition-colors ${
                  isActive
                    ? "text-brand-forest"
                    : "text-brand-mute hover:text-brand-forest"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  {isActive && (
                    <span className="absolute -bottom-0.5 left-4 right-4 h-px bg-brand-gold" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>

        <Link
          to="/visitors"
          data-testid="nav-visitor-pass-btn"
          className="hidden lg:inline-flex items-center px-5 py-2.5 rounded-full bg-brand-forest text-brand-ivory text-sm font-medium hover:bg-brand-leaf transition-colors"
        >
          Visitor Pass
        </Link>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 rounded-lg text-brand-forest"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} weight="light" /> : <List size={26} weight="light" />}
        </button>
      </nav>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-brand-ivory border-t border-brand-sage/70"
        >
          <div className="px-6 py-6 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-base font-medium ${
                    isActive
                      ? "bg-brand-sage text-brand-forest"
                      : "text-brand-mute"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/visitors"
              className="mt-3 inline-flex justify-center items-center px-5 py-3 rounded-full bg-brand-forest text-brand-ivory text-sm font-medium"
            >
              Visitor Pass
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
